import React from 'react';
import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react';
import { Badge, Tooltip } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { HandleDefinition } from '../../types/registry';
import type { NodeTraceEntry } from '../../types/editor';
import type { WrapperSource } from '../../types/dsl';
import { getHandleColor, getHandleFillColor } from '../../utils/handleColors';
import { TraceValueView } from '../../trace/TraceValueView';
import type { TraceLabels } from '../../trace/traceShapes';
import classes from './RuleNode.module.css';

export type RuleNodeData = {
  id: string;
  type: string;
  inputs: HandleDefinition[];
  outputs: HandleDefinition[];
  values?: Record<string, unknown>;
  /** A wrapper's nested `source`, carried so a save cannot drop it. Never rendered. */
  source?: WrapperSource;
  traceEntry?: NodeTraceEntry;
  traceLabels?: TraceLabels;
};

export type RuleNodeProps = {
  id: string;
  data: RuleNodeData;
  selected?: boolean;
};

const NODE_TYPE_COLORS: Record<string, string> = {
  Input: '#FF9E4F',
  Output: '#7FDBF9',
  Logic: '#60a5fa',
  Property: '#a78bfa',
  Template: '#fb7185',
  CodeBlock: '#fbbf24',
  ForEach: '#a6ccf5'
};

/**
 * Format a trace output value for inline display.
 * Primitives are shown directly; objects/arrays are truncated JSON.
 */
function formatTraceValue( value: unknown, maxLen = 80 ): { display: string; truncated: boolean } {
  if ( value === null || value === undefined ) {
    return { display: String( value ), truncated: false };
  }
  if ( typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ) {
    const s = String( value );
    return { display: s.length > maxLen ? `${s.substring( 0, maxLen )}...` : s, truncated: s.length > maxLen };
  }
  const json = JSON.stringify( value );
  return { display: json.length > maxLen ? `${json.substring( 0, maxLen )}...` : json, truncated: json.length > maxLen };
}

function getNodeTypeColor( type: string, hasOutputs: boolean ): string {
  if ( NODE_TYPE_COLORS[type]) {
    return NODE_TYPE_COLORS[type];
  }
  return hasOutputs ? '#DAF8A4' : '#7FDBF9';
}

export function RuleNode({ id, data, selected }: RuleNodeProps ) {
  const update = useUpdateNodeInternals();
  const [ currentIteration, setCurrentIteration ] = React.useState( 0 );

  React.useEffect(() => {
    update( id );
  }, [ id, data.inputs.length, data.outputs.length, update ]);

  const { traceEntry } = data;
  const isFailed = traceEntry?.status === 'error';
  const traceColor = traceEntry?.status === 'success'
    ? '#22c55e'
    : isFailed
      ? '#ef4444'
      : undefined;
  const headerBorderColor = traceColor ?? getNodeTypeColor( data.type, data.outputs.length > 0 );
  const failedBadgeLabel = data.traceLabels?.nodeFailedBadge ?? 'Error';

  const iterationKeys = traceEntry?.iterations
    ? Object.keys( traceEntry.iterations ).map( Number )
.sort(( a, b ) => a - b )
    : [];
  const iterationCount = iterationKeys.length;

  const handleCycleIteration = React.useCallback(() => {
    setCurrentIteration(( prev ) => ( prev + 1 ) % iterationCount );
  }, [iterationCount]);

  return (
    <div
      className={`${classes.container} ${selected ? classes.selected : ''} ${isFailed ? classes.nodeError : ''}`}
    >
      <div
        className={classes.header}
        style={{ borderTopColor: headerBorderColor }}
      >
        <div className={classes.title} title={data.id}>{data.id}</div>
        <div className={classes.headerMeta}>
          <span className={classes.subtitle}>{data.type}</span>
          {isFailed && (
            <Badge color="red" size="xs" variant="filled" className={classes.failedBadge}>
              {failedBadgeLabel}
            </Badge>
          )}
        </div>
      </div>

      <div className={classes.body}>
        <div className={classes.colLeft}>
          {data.inputs.map(( handle ) => (
            <div className={classes.row} key={`in-${handle.name}`}>
              <Handle
                id={handle.name}
                type="target"
                position={Position.Left}
                className={classes.handleIn}
                style={{
                  borderColor: getHandleColor( handle.type ),
                  background: getHandleFillColor( handle.type )
                }}
              />
              <span className={classes.handleLabel} title={handle.name}>
                {handle.name}
                <span
                  className={classes.typeDot}
                  style={{ backgroundColor: getHandleColor( handle.type ) }}
                />
              </span>
            </div>
          ))}
        </div>

        <div className={classes.colRight}>
          {data.outputs.map(( handle ) => (
            <div className={classes.row} key={`out-${handle.name}`}>
              <span className={`${classes.handleLabel} ${classes.rightAlign}`} title={handle.name}>
                {handle.name}
                <span
                  className={classes.typeDot}
                  style={{ backgroundColor: getHandleColor( handle.type ) }}
                />
              </span>
              <Handle
                id={handle.name}
                type="source"
                position={Position.Right}
                className={classes.handleOut}
                style={{
                  borderColor: getHandleColor( handle.type ),
                  background: getHandleFillColor( handle.type )
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {data.values && Object.keys( data.values ).length > 0 && (
        <div className={classes.footer}>
          <div className={classes.valuesTitle}>Values:</div>
          {Object.entries( data.values ).map(([ key, value ]) => (
            <div key={key} className={classes.valueRow}>
              <span className={classes.valueKey} title={key}>{key}:</span>
              <span
                className={classes.valueValue}
                title={typeof value === 'string' ? value : JSON.stringify( value )}
              >
                {(() => {
                  const displayValue = typeof value === 'string' ? value : JSON.stringify( value );
                  return displayValue.length > 40 ? `${displayValue.substring( 0, 40 )}...` : displayValue;
                })()}
              </span>
            </div>
          ))}
        </div>
      )}

      {traceEntry && (
        <div className={classes.traceOutput}>
          <div className={classes.valuesTitle}>Output:</div>
          {isFailed ? (
            <div className={classes.traceErrorBlock}>
              <IconAlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span className={classes.traceErrorMessage}>
                {traceEntry.error ?? failedBadgeLabel}
              </span>
            </div>
          ) : (
            <TraceValueView
              value={traceEntry.output}
              traceLabels={data.traceLabels}
            />
          )}

          {iterationCount > 0 && (
            <div style={{ marginTop: 6 }}>
              <Tooltip label="Click to cycle through iterations">
                <Badge
                  className={classes.iterationBadge}
                  variant="light"
                  color="teal"
                  size="sm"
                  style={{ cursor: 'pointer' }}
                  onClick={handleCycleIteration}
                >
                  {iterationCount} iteration{iterationCount !== 1 ? 's' : ''} (#{iterationKeys[currentIteration]})
                </Badge>
              </Tooltip>
              {traceEntry.iterations && traceEntry.iterations[iterationKeys[currentIteration]] && (
                <div style={{ marginTop: 4 }}>
                  {traceEntry.iterations[iterationKeys[currentIteration]].map(( entry, idx ) => {
                    const { display } = formatTraceValue( entry.output, 50 );
                    return (
                      <div key={idx} className={classes.valueRow}>
                        <span className={classes.valueKey} title={entry.nodeId}>{entry.nodeId}:</span>
                        <span className={classes.traceValue}>{display}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {traceEntry.duration !== undefined && (
            <div className={classes.valuesTitle} style={{ marginTop: 4 }}>
              {traceEntry.duration}ms
            </div>
          )}
        </div>
      )}
    </div>
  );
}
